import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";

import { LanguageProvider } from "@/context/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import { DataProvider } from "@/context/DataContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";

import Home from "@/pages/Home";
import Landing from "@/pages/Landing";
import KeywordPage from "@/pages/KeywordPage";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Tires from "@/pages/Tires";
import TpmsPage from "@/pages/TpmsPage";
import Riyadh from "@/pages/Riyadh";
import NeighborhoodPage from "@/pages/NeighborhoodPage";
import Blog from "@/pages/Blog";
import ArticlePage from "@/pages/ArticlePage";
import About from "@/pages/About";
import FaqPage from "@/pages/FaqPage";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Order from "@/pages/Order";
import AdminPage from "@/admin/AdminPage";
import NotFound from "@/pages/NotFound";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LenisProvider({ children }) {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return children;
}

function SiteChrome() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin") || pathname.startsWith("/lp")) return null;
  return (
    <>
      <Navbar />
      <FloatingActions />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <DataProvider>
            <LenisProvider>
              <BrowserRouter>
                <ScrollToTop />
                <div className="grain min-h-screen bg-ink">
                  <SiteChrome />
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/lp" element={<Landing />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/services/:slug" element={<ServiceDetail />} />
                    <Route path="/tires" element={<Tires />} />
                    <Route path="/tpms" element={<TpmsPage />} />
                    <Route path="/riyadh" element={<Riyadh />} />
                    <Route path="/neighborhood/:slug" element={<NeighborhoodPage />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<ArticlePage />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/faq" element={<FaqPage />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/order" element={<Order />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/:kw" element={<KeywordPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </div>
              </BrowserRouter>
            </LenisProvider>
          </DataProvider>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
